import { Box } from '@chakra-ui/react';
import i18n from '../../i18n';
import { formatMoney } from '../../utils/formatters';
import { formatDate } from '../../utils/intlFormatters';

// Each tone is its colour on a tint of it, with the tint's night value where
// it has one. Spread a tone onto a chip: `{...TONES.green}`. Orange, what
// needs a look, is the checkup's alert colour.
export const TONES = {
  blue:   { color: 'redesign.accent.blue',   bg: 'redesign.bg.accent' },
  green:  { color: 'redesign.amount.income', bg: 'rgba(21, 128, 61, 0.10)', _dark: { bg: 'rgba(74, 222, 128, 0.14)' } },
  orange: { color: 'phone.alertText',        bg: 'phone.alertBg' },
  red:    { color: 'redesign.accent.red',    bg: 'rgba(185, 28, 28, 0.08)', _dark: { bg: 'rgba(248, 113, 113, 0.14)' } },
  muted:  { color: 'redesign.text.muted',    bg: 'redesign.bg.card' },
};

export function Pill({ tone = 'muted', children }) {
  const cfg = TONES[tone] || TONES.muted;
  return (
    <Box
      px={2}
      py="2px"
      borderRadius="full"
      {...cfg}
      fontSize="2xs"
      fontWeight={600}
      flexShrink={0}
      whiteSpace="nowrap"
    >
      {children}
    </Box>
  );
}

export const moneyText = (amount, currency) =>
  (currency ? formatMoney(amount, currency) : formatMoney(amount, null, true));

// "+COP 1,000.00" / "−COP 1,000.00": a change to a balance.
export const signedMoney = (amount, currency) =>
  `${amount < 0 ? '−' : '+'}${moneyText(Math.abs(amount), currency)}`;

// The actions that check a statement: a 409 on them is an import running.
const CHECK_ACTIONS = ['reread'];

// The toast text for a failed action, from the code the API sends with it.
export function errorKey(err, action) {
  const status = err?.response?.status;
  const code = err?.response?.data?.details?.code;
  if (code === 'import_running' || (CHECK_ACTIONS.includes(action) && status === 409)) return 'toasts.importRunning';
  if (code === 'not_rereadable') return 'toasts.notRereadable';
  if (code === 'reread_failed') return 'toasts.rereadFailed';
  if (code === 'not_checkable') return 'toasts.notCheckable';
  if (code === 'ledger_changed') return 'toasts.ledgerChanged';
  if (code === 'nothing_accepted') return 'toasts.nothingAccepted';
  if (code === 'decisions_closed') return 'toasts.decisionsClosed';
  if (code === 'not_checked') return 'toasts.notChecked';
  if (code === 'decisions_open') return 'toasts.decisionsOpen';
  if (code === 'nothing_to_review') return 'toasts.nothingToReview';
  if (status === 403) return 'errors.forbidden';
  return 'toasts.error';
}

/**
 * A statement's flags (one row of `GET /repairs/statements`), in the order
 * its chips show them: each is a key of `repairs:flags` and whether it asks
 * for attention.
 */
export function statementFlags(flags) {
  if (!flags) return [];
  // Reviewed as it is: still said, no longer asking for a look.
  const reviewed = Boolean(flags.reviewed);
  return [
    // The account's balance on its closing date is the bank's: nothing to fix.
    flags.adds_up === false && { key: 'doesntAddUp', attention: !reviewed && !flags.balance_matches },
    flags.balance_matches && { key: 'balanceMatches', attention: false },
    flags.reprocess_suggested && { key: 'reprocess', attention: !reviewed },
    flags.leftover_rows > 0 && { key: 'leftovers', attention: !reviewed },
    flags.repaired && { key: 'repaired', attention: false, good: true },
    reviewed && { key: 'reviewed', attention: false },
  ].filter(Boolean);
}

// "COP 728,000.00": how far a statement lands from its closing, unsigned.
export const offByText = (offBy) => moneyText(Math.abs(offBy.amount), offBy.currency);

// ── Summary lines ────────────────────────────────────────────────────────────
// Each line of an item's summary is a code plus params; `repairs:lines.<code>`
// is its template in the reader's language. A placeholder names a param and,
// optionally, how to render it: `{{account_id, account}}` is the account's
// name. The placeholders are filled here rather than by i18next's formatters,
// which are global, lower-case their names and skip null values ("no
// currency" is a value these lines need).

export const LINE_PLACEHOLDER = /\{\{\s*(\w+)\s*(?:,\s*(\w+)\s*)?\}\}/g;

const joinAnd = (t, parts) =>
  (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} ${t('words.and')} ${parts[parts.length - 1]}`);

const cardAmount = (card, t) =>
  (card.amount == null ? t('fragments.noAmount') : moneyText(Math.abs(card.amount), card.currency));

export const LINE_FORMATS = {
  account: (id, ctx) => ctx.accountName?.(id) || ctx.t('fragments.account', { id }),
  date: (value, { t }) => formatDate(value) || t('fragments.noDate'),
  money: (amount) => formatMoney(Number(amount) || 0, null, true),
  currency: (code, { t }) => code || t('fragments.noCurrency'),
  ids: (ids) => (ids || []).map((id) => `#${id}`).join(', '),
  direction: (sign, { t }) => t(`words.direction.${sign}`, { defaultValue: sign }),
  reasons: (codes, { t }) => (codes || []).map((code) => t(`words.reason.${code}`, { defaultValue: code })).join('; '),
  // What a re-read reads differently in a row: sign, amount, date, description.
  changes: (kinds, { t }) => joinAnd(t, (kinds || []).map((kind) => t(`diff.change.${kind}`, { defaultValue: kind }))),
  movement: (card, ctx) => {
    const { t } = ctx;
    let accounts = t('fragments.noAccount');
    if (card.transfer) {
      accounts = t('fragments.transfer', {
        from: LINE_FORMATS.account(card.from_account_id, ctx),
        to: LINE_FORMATS.account(card.to_account_id, ctx),
      });
    } else if (card.account_id != null) {
      accounts = LINE_FORMATS.account(card.account_id, ctx);
    }
    return t('fragments.movement', {
      id: card.id, date: LINE_FORMATS.date(card.date, ctx), amount: cardAmount(card, t), accounts,
    });
  },
  row: (card, ctx) => ctx.t('fragments.row', {
    account: LINE_FORMATS.account(card.account_id, ctx),
    date: LINE_FORMATS.date(card.date, ctx),
    amount: cardAmount(card, ctx.t),
  }),
};

/**
 * One summary line in the reader's language, or null when the code has no
 * template. A line with a `count` may have a `_one` / `_other` pair instead.
 * `ctx.accountName(id)` gives the name an account id is shown as.
 */
export function renderLine(t, line, ctx = {}) {
  const params = line.params || {};
  const plural = typeof params.count === 'number' ? (params.count === 1 ? '_one' : '_other') : '';
  const key = [`lines.${line.code}`, `lines.${line.code}${plural}`]
    .find((candidate) => i18n.exists(`repairs:${candidate}`));
  if (!key) return null;
  const formatCtx = { ...ctx, t };
  return t(key, { skipInterpolation: true }).replace(LINE_PLACEHOLDER, (match, name, format) => {
    const value = params[name];
    if (format && LINE_FORMATS[format]) return LINE_FORMATS[format](value, formatCtx);
    return value == null ? '' : String(value);
  });
}

export const SECTIONS = ['keep', 'lose', 'gain', 'asks'];

/**
 * An item's summary as sentences per section. The planner writes each
 * sentence to its section's English list and to `lines` in one step, so the
 * n-th line of a section is the n-th sentence of that list: the English
 * sentence stands in for a code this app has no template for. A summary
 * without lines is shown as its English lists.
 */
export function summarySentences(summary, t, ctx) {
  const out = Object.fromEntries(SECTIONS.map((section) => [section, []]));
  if (!summary?.lines?.length) {
    SECTIONS.forEach((section) => out[section].push(...(summary?.[section] || [])));
    return out;
  }
  summary.lines.forEach((line) => {
    const list = out[line.section];
    if (!list) return;
    const sentence = renderLine(t, line, ctx) ?? summary[line.section]?.[list.length];
    list.push(sentence || '');
  });
  return out;
}
